// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Four routes for one subject. The detail view is a page and not a modal,
// because a job is a place people link each other to, come back to, and keep
// open in a tab for months.

export const routes = [
  {
    path: '/projects',
    name: 'projects',
    component: () => import('./pages/ProjectsPage.vue'),
    meta: { permission: 'projects:read', title: 'projects.title' , ownsQuery: true /* limpia la consulta después de leerla: el armazón no la remonta al cambiar */ },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but new sits first anyway, for the reader.
  {
    path: '/projects/new',
    name: 'project-new',
    component: () => import('./pages/ProjectFormPage.vue'),
    meta: { permission: 'projects:write', title: 'projects.newTitle' },
  },
  {
    path: '/projects/:id',
    name: 'project',
    component: () => import('./pages/ProjectPage.vue'),
    meta: { permission: 'projects:read', title: 'projects.detailTitle' },
  },
  {
    path: '/projects/:id/edit',
    name: 'project-edit',
    component: () => import('./pages/ProjectFormPage.vue'),
    meta: { permission: 'projects:write', title: 'projects.editTitle' },
  },
]

/**
 * One row, no children: the whole module happens on the projects screen. The
 * shell filters it by permission before drawing it.
 */
export const navigation = [
  {
    to: '/projects',
    labelKey: 'projects.title',
    permission: 'projects:read',
    icon: 'gantt',
    group: 'business',
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold
// to the third client. That includes what this module is called on the
// PLATFORM's own screens — Roles, Settings, Audit and the assistant panel —
// because a module that makes somebody add keys to the shell is not extractable.
const baseMessages = {
  es: {
    projects: {
      title: 'Proyectos',
      dashboard: {
        open: 'Proyectos abiertos',
        openHelp: 'Cuántos trabajos siguen en marcha, sin contar los cerrados ni los cancelados.',
        late: 'Proyectos con retraso',
        lateHelp: 'Cuántos trabajos abiertos han pasado de la fecha comprometida con el cliente.',
        contracted: 'Contratado en marcha',
        contractedHelp: 'Suma de lo vendido en los proyectos abiertos, en euros.',
        toInvoice: 'Por facturar',
        toInvoiceHelp: 'De lo vendido en proyectos abiertos, lo que todavía no se ha facturado.',
        openList: 'Trabajos en marcha',
        openListHelp: 'Los proyectos abiertos más recientes, con su cliente. Estira la tarjeta y caben más.',
      },
      subtitle: 'Los trabajos en marcha, en qué fase están y qué se llevan',
      detailTitle: 'Proyecto',
      newTitle: 'Nuevo proyecto',
      editTitle: 'Editar proyecto',
      actions: {
        new: 'Nuevo proyecto', edit: 'Editar', postCost: 'Apuntar coste',
        close: 'Cerrar proyecto', reopen: 'Reabrir', cancel: 'Cancelar',
        save: 'Guardar', remove: 'Eliminar',
      },
      status: {
        draft: 'Borrador', active: 'En marcha', on_hold: 'Parado',
        closed: 'Cerrado', cancelled: 'Anulado',
      },
      priority: { low: 'Baja', medium: 'Normal', high: 'Alta', critical: 'Crítica' },
      nature: {
        material: 'Material', labour: 'Mano de obra',
        subcontract: 'Subcontrata', other: 'Otros',
      },
      kind: { incurred: 'Ya nos ha llegado', committed: 'Pedido, aún no llegado' },
      filters: {
        oneCustomer: 'Solo de un cliente',

        search: 'Buscar', searchPlaceholder: 'Código, obra, cliente, lugar…',
        status: 'Estado', allStatuses: 'Todos',
        priority: 'Prioridad', allPriorities: 'Todas',
        onlyOpen: 'Solo abiertos', onlyLate: 'Solo con retraso',
      },
      board: { title: 'Dónde está cada trabajo' },
      table: {
        code: 'Código', name: 'Proyecto', phase: 'Fase', due: 'Entrega',
        amount: 'Importe', margin: 'Margen', status: 'Estado',
      },
      kpi: {
        open: 'Proyectos abiertos', late: 'Con retraso',
        exposure: 'Coste comprometido', contract: 'Contratado',
      },
      empty: {
        none: 'Todavía no hay proyectos',
        noneHint: 'Abre el primero, o acepta un presupuesto y deja que se abra solo.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
      },
      lateBy: '{days} días de retraso',
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },
      tabs: {
        overview: 'Resumen', quotes: 'Presupuestos', documents: 'Documentos',
        costs: 'Costes', history: 'Historial',
      },
      phases: {
        title: 'Fases',
        since: 'En esta fase desde el {date}',
        moved: 'Proyecto en {phase}',
      },
      file: { title: 'Ficha' },
      fields: {
        code: 'Código', name: 'Nombre', customer: 'Cliente', location: 'Lugar',
        manager: 'Responsable', priority: 'Prioridad', phase: 'Fase', tags: 'Etiquetas',
        description: 'Descripción', startDate: 'Inicio', dueDate: 'Entrega comprometida',
        estimatedEndDate: 'Fin previsto', quote: 'Presupuesto',
        contractAmount: 'Importe de venta', budgetedCost: 'Coste previsto',
      },
      money: {
        title: 'Económica',
        contract: 'Vendido (sin IVA)', exposure: 'Comprometido', margin: 'Margen',
        pendingToInvoice: 'Por facturar',
        ofBudget: '{pct} del previsto',
        incurred: 'Gastado', committed: 'Pedido sin llegar',
        overBudget: 'Este proyecto ha superado su coste previsto ({budget})',
      },
      ledger: {
        title: 'Costes',
        empty: 'Sin costes todavía',
        emptyHint: 'Apunta el primero, o llegará solo cuando entre material a este proyecto.',
        date: 'Fecha', description: 'Concepto', amount: 'Importe',
        from: 'de {module}',
      },
      quotes: {
        title: 'Presupuestos del proyecto',
        line: 'Partida', quantity: 'Cantidad', unitPrice: 'Precio', total: 'Total',
        noLines: 'Este presupuesto no tiene partidas guardadas.',
        empty: 'Sin presupuestos vinculados',
        emptyHint: 'Vincúlalos desde el propio presupuesto: campo «Proyecto» al redactarlo, o la tarjeta «Proyecto» si ya está aceptado.',
      },
      documents: {
        title: 'Documentos',
        all: 'Todos los documentos', root: 'Sin carpeta',
        upload: 'Subir documento', uploaded: 'Documento subido',
        newFolder: 'Nueva carpeta', folderName: 'Nombre de la carpeta…',
        removeFolder: 'Eliminar carpeta',
        empty: 'Todavía no hay documentos',
        emptyFolder: 'Esta carpeta está vacía',
        emptyHint: 'Sube planos, actas, certificados, fotos… lo que el proyecto necesite tener a mano.',
        confirmRemoveTitle: 'Eliminar el documento',
        confirmRemoveBody: 'Se eliminará «{name}» de este proyecto.',
        searchPlaceholder: 'Buscar documento…',
        name: 'Nombre', size: 'Tamaño', uploadedAt: 'Subido',
        download: 'Descargar',
        noResults: 'Nada con ese nombre',
        dropHint: 'Arrastra ficheros aquí para subirlos',
        uploadedMany: '{count} documentos subidos',
      },
      history: {
        title: 'Historial',
        notePlaceholder: 'Escribe una nota en el proyecto…',
        add: 'Anotar',
        phase: 'Fase: {from} → {to}',
        status: 'Estado: {from} → {to}',
      },
      confirm: {
        closeTitle: 'Cerrar el proyecto',
        closeBody: 'El margen queda como está ahora y saldrá en los informes. Se puede reabrir.',
        removeCostTitle: 'Eliminar el apunte',
        removeCostBody: 'Se eliminará el apunte de {amount} y se recalculará el proyecto.',
      },
      closed: 'Proyecto cerrado',
      reopened: 'Proyecto reabierto',
      createdToast: 'Proyecto {code} creado',
      savedToast: 'Proyecto guardado',
      cost: {
        title: 'Apuntar un coste',
        nature: 'Naturaleza', kind: '¿Ha llegado ya?',
        description: 'Concepto', descriptionPlaceholder: 'Grúa para la descarga…',
        quantity: 'Cantidad', unit: 'Unidad', unitCost: 'Precio unitario',
        amount: 'Importe', amountComputed: 'Cantidad × precio. Se calcula solo.',
        amountFromRate: 'Sin precio, las horas las valora la empresa. Puede quedar en 0.',
        hoursHint: 'p. ej. 6',
        date: 'Fecha', dateHelp: 'La fecha a la que pertenece el coste, no la de hoy.',
        post: 'Apuntar', posted: 'Coste apuntado',
      },
      form: {
        what: 'El trabajo', when: 'Las fechas', money: 'El dinero',
        namePlaceholder: 'Cerramientos chalet García…',
        customerPlaceholder: 'Para quién es',
        locationPlaceholder: 'Dónde está la obra o el trabajo',
        tagsHelp: 'Separadas por comas.',
        tagsPlaceholder: 'obra, urgente…',
        dueHelp: 'La del contrato. La que tiene penalización detrás.',
        contractHelp: 'Por cuánto se ha vendido, sin IVA: los costes también son sin IVA.',
        budgetHelp: 'Cuánto se espera que cueste. El aviso de desvío se mide contra esto.',
        nameRequired: 'El proyecto necesita un nombre',
      },
      search: { subtitle: 'Abrir el proyecto' },
      notifications: {
        categories: { overrun: 'Proyectos: desvío de coste' },
        overrun: {
          title: 'Proyecto pasado de coste',
          body: '{code} {name} va por el {ratio} % de su coste previsto',
        },
      },
    },
    // ── Lo que este módulo se llama en las pantallas de la plataforma ─────────
    // Roles, ajustes y auditoría son de la plataforma, pero los textos que
    // hablan de proyectos son de este módulo. Van aquí para que instalarlo deje
    // esas tres pantallas completas sin tocar la plataforma.
    modules: { projects: 'Proyectos' },
    assistant: {
      openers: {
        projects: {
          'whats-late': '¿Qué proyectos van con retraso?',
          'how-is-going': '¿Cómo va el proyecto de…?',
          'post-hours': 'Apunta 6 horas al proyecto…',
        },
      },
    },
    permissions: {
      projects: {
        read: 'Ver los proyectos: fases, fechas, equipo',
        write: 'Abrir proyectos, editarlos y moverlos de fase',
        costs: 'Apuntar costes y ver presupuesto, coste y margen',
        close: 'Cerrar, reabrir, anular y archivar proyectos',
      },
    },
    settings: {
      ns: {
        projects: {
          title: 'Proyectos',
          subtitle: 'Cómo trabaja vuestra empresa un proyecto',
          phases: {
            label: 'Las fases de un trabajo, en orden',
            help: 'Vuestras: renombrad, añadid, quitad. Renombrar una deja los proyectos que pasaron por ella mostrando el nombre antiguo en su historia.',
          },
          customFields: {
            label: 'Vuestros campos propios en la ficha',
            help: 'Por ejemplo: nº de pedido del cliente, centro de coste, delegación. Renombrar uno deja atrás lo guardado con el nombre antiguo.',
          },
          createFromAcceptedQuote: {
            label: 'Abrir proyecto solo al aceptarse un presupuesto',
            help: 'Solo hace algo con el módulo de presupuestos instalado.',
          },
          requireCustomer: {
            label: 'Exigir cliente en todos los proyectos',
            help: 'Dejadlo desactivado si abrís trabajos para vosotros mismos: una reforma del taller, un stand.',
          },
          overrunAlertPct: {
            label: 'Avisar cuando un trabajo llegue a este % de su coste previsto',
            help: 'Se mide contra lo comprometido, no solo contra lo llegado, para avisar cuando aún se puede decidir algo. 0 lo apaga.',
          },
          alertRoles: {
            label: 'Quién se entera de que un proyecto se pasa de coste',
            help: 'Roles, no personas, para que siga funcionando cuando alguien se va.',
          },
          retentionPct: {
            label: 'Retención de garantía sobre lo facturado',
            help: 'Habitual en obra, inexistente en taller. 0 la apaga.',
          },
        },
      },
    },
    audit: {
      actions: {
        projects: {
          project_created: 'Proyecto abierto',
          project_updated: 'Proyecto modificado',
          project_note_added: 'Nota añadida al proyecto',
          project_phase_changed: 'Proyecto cambiado de fase',
          project_active: 'Proyecto puesto en marcha',
          project_on_hold: 'Proyecto parado',
          project_draft: 'Proyecto devuelto a borrador',
          project_closed: 'Proyecto cerrado',
          project_cancelled: 'Proyecto anulado',
          project_archived: 'Proyecto archivado',
          project_restored: 'Proyecto devuelto a las listas',
          project_cost_posted: 'Coste apuntado al proyecto',
          project_cost_removed: 'Coste eliminado del proyecto',
          project_document_uploaded: 'Documento subido al proyecto',
          project_document_moved: 'Documento cambiado de carpeta',
          project_document_removed: 'Documento eliminado del proyecto',
          project_folder_created: 'Carpeta creada en el proyecto',
          project_folder_removed: 'Carpeta eliminada del proyecto',
        },
      },
    },
  },

  ca: {
    projects: {
      title: 'Projectes',
      dashboard: {
        open: 'Projectes oberts',
        openHelp: 'Quants treballs segueixen en marxa, sense comptar els tancats ni els cancel·lats.',
        late: 'Projectes amb retard',
        lateHelp: 'Quants treballs oberts han passat de la data compromesa amb el client.',
        contracted: 'Contractat en marxa',
        contractedHelp: 'Suma del que s\'ha venut en els projectes oberts, en euros.',
        toInvoice: 'Per facturar',
        toInvoiceHelp: 'Del que s\'ha venut en projectes oberts, el que encara no s\'ha facturat.',
        openList: 'Treballs en marxa',
        openListHelp: 'Els projectes oberts més recents, amb el seu client. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'Les feines en marxa, en quina fase són i què s\'emporten',
      detailTitle: 'Projecte',
      newTitle: 'Nou projecte',
      editTitle: 'Editar projecte',
      actions: {
        new: 'Nou projecte', edit: 'Editar', postCost: 'Apuntar cost',
        close: 'Tancar projecte', reopen: 'Reobrir', cancel: 'Cancel·lar',
        save: 'Desar', remove: 'Eliminar',
      },
      status: {
        draft: 'Esborrany', active: 'En marxa', on_hold: 'Aturat',
        closed: 'Tancat', cancelled: 'Anul·lat',
      },
      priority: { low: 'Baixa', medium: 'Normal', high: 'Alta', critical: 'Crítica' },
      nature: {
        material: 'Material', labour: 'Mà d\'obra',
        subcontract: 'Subcontracta', other: 'Altres',
      },
      kind: { incurred: 'Ja ens ha arribat', committed: 'Demanat, encara no arribat' },
      filters: {
        oneCustomer: "Només d'un client",

        search: 'Cercar', searchPlaceholder: 'Codi, obra, client, lloc…',
        status: 'Estat', allStatuses: 'Tots',
        priority: 'Prioritat', allPriorities: 'Totes',
        onlyOpen: 'Només oberts', onlyLate: 'Només amb retard',
      },
      board: { title: 'On és cada feina' },
      table: {
        code: 'Codi', name: 'Projecte', phase: 'Fase', due: 'Lliurament',
        amount: 'Import', margin: 'Marge', status: 'Estat',
      },
      kpi: {
        open: 'Projectes oberts', late: 'Amb retard',
        exposure: 'Cost compromès', contract: 'Contractat',
      },
      empty: {
        none: 'Encara no hi ha projectes',
        noneHint: 'Obre el primer, o accepta un pressupost i deixa que s\'obri sol.',
        filtered: 'Sense resultats',
        filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
      },
      lateBy: '{days} dies de retard',
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      tabs: {
        overview: 'Resum', quotes: 'Pressupostos', documents: 'Documents',
        costs: 'Costos', history: 'Historial',
      },
      phases: {
        title: 'Fases',
        since: 'En aquesta fase des del {date}',
        moved: 'Projecte a {phase}',
      },
      file: { title: 'Fitxa' },
      fields: {
        code: 'Codi', name: 'Nom', customer: 'Client', location: 'Lloc',
        manager: 'Responsable', priority: 'Prioritat', phase: 'Fase', tags: 'Etiquetes',
        description: 'Descripció', startDate: 'Inici', dueDate: 'Lliurament compromès',
        estimatedEndDate: 'Final previst', quote: 'Pressupost',
        contractAmount: 'Import de venda', budgetedCost: 'Cost previst',
      },
      money: {
        title: 'Econòmica',
        contract: 'Venut (sense IVA)', exposure: 'Compromès', margin: 'Marge',
        pendingToInvoice: 'Per facturar',
        ofBudget: '{pct} del previst',
        incurred: 'Gastat', committed: 'Demanat sense arribar',
        overBudget: 'Aquest projecte ha superat el seu cost previst ({budget})',
      },
      ledger: {
        title: 'Costos',
        empty: 'Sense costos encara',
        emptyHint: 'Apunta el primer, o arribarà sol quan entri material a aquest projecte.',
        date: 'Data', description: 'Concepte', amount: 'Import',
        from: 'de {module}',
      },
      quotes: {
        title: 'Pressupostos del projecte',
        line: 'Partida', quantity: 'Quantitat', unitPrice: 'Preu', total: 'Total',
        noLines: 'Aquest pressupost no té partides desades.',
        empty: 'Sense pressupostos vinculats',
        emptyHint: "Vincula'ls des del mateix pressupost: camp «Projecte» en redactar-lo, o la targeta «Projecte» si ja està acceptat.",
      },
      documents: {
        title: 'Documents',
        all: 'Tots els documents', root: 'Sense carpeta',
        upload: 'Pujar document', uploaded: 'Document pujat',
        newFolder: 'Nova carpeta', folderName: 'Nom de la carpeta…',
        removeFolder: 'Eliminar carpeta',
        empty: 'Encara no hi ha documents',
        emptyFolder: 'Aquesta carpeta és buida',
        emptyHint: 'Puja plànols, actes, certificats, fotos… el que el projecte necessiti tenir a mà.',
        confirmRemoveTitle: 'Eliminar el document',
        confirmRemoveBody: "S'eliminarà «{name}» d'aquest projecte.",
        searchPlaceholder: 'Cercar document…',
        name: 'Nom', size: 'Mida', uploadedAt: 'Pujat',
        download: 'Descarregar',
        noResults: 'Res amb aquest nom',
        dropHint: 'Arrossega fitxers aquí per pujar-los',
        uploadedMany: '{count} documents pujats',
      },
      history: {
        title: 'Historial',
        notePlaceholder: 'Escriu una nota al projecte…',
        add: 'Anotar',
        phase: 'Fase: {from} → {to}',
        status: 'Estat: {from} → {to}',
      },
      confirm: {
        closeTitle: 'Tancar el projecte',
        closeBody: 'El marge queda com està ara i sortirà als informes. Es pot reobrir.',
        removeCostTitle: 'Eliminar l\'apunt',
        removeCostBody: 'S\'eliminarà l\'apunt de {amount} i es recalcularà el projecte.',
      },
      closed: 'Projecte tancat',
      reopened: 'Projecte reobert',
      createdToast: 'Projecte {code} creat',
      savedToast: 'Projecte desat',
      cost: {
        title: 'Apuntar un cost',
        nature: 'Naturalesa', kind: 'Ha arribat ja?',
        description: 'Concepte', descriptionPlaceholder: 'Grua per a la descàrrega…',
        quantity: 'Quantitat', unit: 'Unitat', unitCost: 'Preu unitari',
        amount: 'Import', amountComputed: 'Quantitat × preu. Es calcula sol.',
        amountFromRate: 'Sense preu, les hores les valora l\'empresa. Pot quedar a 0.',
        hoursHint: 'p. ex. 6',
        date: 'Data', dateHelp: 'La data a què pertany el cost, no la d\'avui.',
        post: 'Apuntar', posted: 'Cost apuntat',
      },
      form: {
        what: 'La feina', when: 'Les dates', money: 'Els diners',
        namePlaceholder: 'Tancaments xalet García…',
        customerPlaceholder: 'Per a qui és',
        locationPlaceholder: 'On és l\'obra o la feina',
        tagsHelp: 'Separades per comes.',
        tagsPlaceholder: 'obra, urgent…',
        dueHelp: 'La del contracte. La que té penalització al darrere.',
        contractHelp: 'Per quant s\'ha venut, sense IVA: els costos també són sense IVA.',
        budgetHelp: 'Quant s\'espera que costi. L\'avís de desviació es mesura contra això.',
        nameRequired: 'El projecte necessita un nom',
      },
      search: { subtitle: 'Obrir el projecte' },
      notifications: {
        categories: { overrun: 'Projectes: desviació de cost' },
        overrun: {
          title: 'Projecte passat de cost',
          body: '{code} {name} va pel {ratio} % del seu cost previst',
        },
      },
    },
    modules: { projects: 'Projectes' },
    assistant: {
      openers: {
        projects: {
          'whats-late': 'Quins projectes van amb retard?',
          'how-is-going': 'Com va el projecte de…?',
          'post-hours': 'Apunta 6 hores al projecte…',
        },
      },
    },
    permissions: {
      projects: {
        read: 'Veure els projectes: fases, dates, equip',
        write: 'Obrir projectes, editar-los i moure\'ls de fase',
        costs: 'Apuntar costos i veure pressupost, cost i marge',
        close: 'Tancar, reobrir, anul·lar i arxivar projectes',
      },
    },
    settings: {
      ns: {
        projects: {
          title: 'Projectes',
          subtitle: 'Com treballa la vostra empresa un projecte',
          phases: {
            label: 'Les fases d\'una feina, en ordre',
            help: 'Vostres: reanomeneu, afegiu, traieu. Reanomenar-ne una deixa els projectes que hi van passar mostrant el nom antic a la seva història.',
          },
          customFields: {
            label: 'Els vostres camps propis a la fitxa',
            help: 'Per exemple: núm. de comanda del client, centre de cost, delegació. Reanomenar-ne un deixa enrere el que hi havia desat amb el nom antic.',
          },
          createFromAcceptedQuote: {
            label: 'Obrir projecte sol en acceptar-se un pressupost',
            help: 'Només fa alguna cosa amb el mòdul de pressupostos instal·lat.',
          },
          requireCustomer: {
            label: 'Exigir client a tots els projectes',
            help: 'Deixeu-ho desactivat si obriu feines per a vosaltres mateixos: una reforma del taller, un estand.',
          },
          overrunAlertPct: {
            label: 'Avisar quan una feina arribi a aquest % del seu cost previst',
            help: 'Es mesura contra el compromès, no només contra l\'arribat, per avisar quan encara es pot decidir alguna cosa. 0 l\'apaga.',
          },
          alertRoles: {
            label: 'Qui s\'assabenta que un projecte es passa de cost',
            help: 'Rols, no persones, perquè continuï funcionant quan algú marxa.',
          },
          retentionPct: {
            label: 'Retenció de garantia sobre el facturat',
            help: 'Habitual en obra, inexistent en taller. 0 l\'apaga.',
          },
        },
      },
    },
    audit: {
      actions: {
        projects: {
          project_created: 'Projecte obert',
          project_updated: 'Projecte modificat',
          project_note_added: 'Nota afegida al projecte',
          project_phase_changed: 'Projecte canviat de fase',
          project_active: 'Projecte posat en marxa',
          project_on_hold: 'Projecte aturat',
          project_draft: 'Projecte tornat a esborrany',
          project_closed: 'Projecte tancat',
          project_cancelled: 'Projecte anul·lat',
          project_archived: 'Projecte arxivat',
          project_restored: 'Projecte retornat a les llistes',
          project_cost_posted: 'Cost apuntat al projecte',
          project_cost_removed: 'Cost eliminat del projecte',
          project_document_uploaded: 'Document pujat al projecte',
          project_document_moved: 'Document canviat de carpeta',
          project_document_removed: 'Document eliminat del projecte',
          project_folder_created: 'Carpeta creada al projecte',
          project_folder_removed: 'Carpeta eliminada del projecte',
        },
      },
    },
  },

  en: {
    projects: {
      title: 'Projects',
      dashboard: {
        open: 'Open projects',
        openHelp: 'How many jobs are still running, closed and cancelled ones excluded.',
        late: 'Projects running late',
        lateHelp: 'How many open jobs are past the date agreed with the customer.',
        contracted: 'Contracted, in progress',
        contractedHelp: 'What has been sold on the open projects, in euros.',
        toInvoice: 'Left to invoice',
        toInvoiceHelp: 'Of what has been sold on open projects, what has not been invoiced yet.',
        openList: 'Jobs in progress',
        openListHelp: 'The most recent open projects, with their customer. Stretch the card and more fit.',
      },
      subtitle: 'The jobs under way, what phase they are in and what they are taking',
      detailTitle: 'Project',
      newTitle: 'New project',
      editTitle: 'Edit project',
      actions: {
        new: 'New project', edit: 'Edit', postCost: 'Post a cost',
        close: 'Close project', reopen: 'Reopen', cancel: 'Cancel',
        save: 'Save', remove: 'Remove',
      },
      status: {
        draft: 'Draft', active: 'Under way', on_hold: 'On hold',
        closed: 'Closed', cancelled: 'Cancelled',
      },
      priority: { low: 'Low', medium: 'Normal', high: 'High', critical: 'Critical' },
      nature: {
        material: 'Material', labour: 'Labour',
        subcontract: 'Subcontract', other: 'Other',
      },
      kind: { incurred: 'Already arrived', committed: 'Ordered, not yet arrived' },
      filters: {
        oneCustomer: 'One customer only',

        search: 'Search', searchPlaceholder: 'Code, job, customer, place…',
        status: 'Status', allStatuses: 'All',
        priority: 'Priority', allPriorities: 'All',
        onlyOpen: 'Only open', onlyLate: 'Only late',
      },
      board: { title: 'Where every job is' },
      table: {
        code: 'Code', name: 'Project', phase: 'Phase', due: 'Delivery',
        amount: 'Amount', margin: 'Margin', status: 'Status',
      },
      kpi: {
        open: 'Open projects', late: 'Late',
        exposure: 'Committed cost', contract: 'Contracted',
      },
      empty: {
        none: 'No projects yet',
        noneHint: 'Open the first one, or accept a quote and let it open itself.',
        filtered: 'No results',
        filteredHint: 'Try removing a filter or searching for something else.',
      },
      lateBy: '{days} days late',
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      tabs: {
        overview: 'Overview', quotes: 'Quotes', documents: 'Documents',
        costs: 'Costs', history: 'History',
      },
      phases: {
        title: 'Phases',
        since: 'In this phase since {date}',
        moved: 'Project now in {phase}',
      },
      file: { title: 'File' },
      fields: {
        code: 'Code', name: 'Name', customer: 'Customer', location: 'Place',
        manager: 'Manager', priority: 'Priority', phase: 'Phase', tags: 'Tags',
        description: 'Description', startDate: 'Start', dueDate: 'Contractual delivery',
        estimatedEndDate: 'Expected end', quote: 'Quote',
        contractAmount: 'Sale amount', budgetedCost: 'Budgeted cost',
      },
      money: {
        title: 'Financials',
        contract: 'Sold (before VAT)', exposure: 'Committed', margin: 'Margin',
        pendingToInvoice: 'Left to invoice',
        ofBudget: '{pct} of budget',
        incurred: 'Spent', committed: 'Ordered, not arrived',
        overBudget: 'This project has gone past its budgeted cost ({budget})',
      },
      ledger: {
        title: 'Costs',
        empty: 'No costs yet',
        emptyHint: 'Post the first one, or it will arrive on its own when material comes in for this project.',
        date: 'Date', description: 'Description', amount: 'Amount',
        from: 'from {module}',
      },
      quotes: {
        title: 'Quotes on this project',
        line: 'Line item', quantity: 'Quantity', unitPrice: 'Price', total: 'Total',
        noLines: 'This quote has no stored line items.',
        empty: 'No linked quotes',
        emptyHint: 'Link them from the quote itself: the Project field while drafting, or the Project card once accepted.',
      },
      documents: {
        title: 'Documents',
        all: 'All documents', root: 'No folder',
        upload: 'Upload document', uploaded: 'Document uploaded',
        newFolder: 'New folder', folderName: 'Folder name…',
        removeFolder: 'Remove folder',
        empty: 'No documents yet',
        emptyFolder: 'This folder is empty',
        emptyHint: 'Upload plans, signed acceptances, certificates, photos… whatever the job needs at hand.',
        confirmRemoveTitle: 'Remove the document',
        confirmRemoveBody: '“{name}” will be removed from this project.',
        searchPlaceholder: 'Search documents…',
        name: 'Name', size: 'Size', uploadedAt: 'Uploaded',
        download: 'Download',
        noResults: 'Nothing by that name',
        dropHint: 'Drag files here to upload them',
        uploadedMany: '{count} documents uploaded',
      },
      history: {
        title: 'History',
        notePlaceholder: 'Write a note on the project…',
        add: 'Add',
        phase: 'Phase: {from} → {to}',
        status: 'Status: {from} → {to}',
      },
      confirm: {
        closeTitle: 'Close the project',
        closeBody: 'The margin stays as it is now and goes into the reports. It can be reopened.',
        removeCostTitle: 'Remove the entry',
        removeCostBody: 'The {amount} entry will be removed and the project recalculated.',
      },
      closed: 'Project closed',
      reopened: 'Project reopened',
      createdToast: 'Project {code} created',
      savedToast: 'Project saved',
      cost: {
        title: 'Post a cost',
        nature: 'Nature', kind: 'Has it arrived?',
        description: 'Description', descriptionPlaceholder: 'Crane for the unloading…',
        quantity: 'Quantity', unit: 'Unit', unitCost: 'Unit cost',
        amount: 'Amount', amountComputed: 'Quantity × cost. Computed for you.',
        amountFromRate: 'With no rate, the company prices the hours. It may stay at 0.',
        hoursHint: 'e.g. 6',
        date: 'Date', dateHelp: 'The date the cost belongs to, not today\'s.',
        post: 'Post', posted: 'Cost posted',
      },
      form: {
        what: 'The job', when: 'The dates', money: 'The money',
        namePlaceholder: 'García house enclosures…',
        customerPlaceholder: 'Who it is for',
        locationPlaceholder: 'Where the job is',
        tagsHelp: 'Separated by commas.',
        tagsPlaceholder: 'site, urgent…',
        dueHelp: 'The contractual one. The one with a penalty behind it.',
        contractHelp: 'What it was sold for, before VAT: costs are before VAT too.',
        budgetHelp: 'What it is expected to cost. The overrun warning is measured against this.',
        nameRequired: 'A project needs a name',
      },
      search: { subtitle: 'Open the project' },
      notifications: {
        categories: { overrun: 'Projects: cost overrun' },
        overrun: {
          title: 'Project over budget',
          body: '{code} {name} is at {ratio} % of its budgeted cost',
        },
      },
    },
    modules: { projects: 'Projects' },
    assistant: {
      openers: {
        projects: {
          'whats-late': 'Which projects are late?',
          'how-is-going': 'How is the … project going?',
          'post-hours': 'Post 6 hours to the … project',
        },
      },
    },
    permissions: {
      projects: {
        read: 'See the projects: phases, dates, team',
        write: 'Open projects, edit them and move them along',
        costs: 'Post costs and see budget, cost and margin',
        close: 'Close, reopen, cancel and archive projects',
      },
    },
    settings: {
      ns: {
        projects: {
          title: 'Projects',
          subtitle: 'How your company runs a job',
          phases: {
            label: 'The phases a job goes through, in order',
            help: 'Yours: rename them, add them, remove them. Renaming one leaves the jobs that passed through it showing the old name in their history.',
          },
          customFields: {
            label: 'Your own fields on the file',
            help: 'For example: the customer\'s order number, a cost centre, a branch. Renaming one leaves what was stored under the old name behind.',
          },
          createFromAcceptedQuote: {
            label: 'Open a project on its own when a quote is accepted',
            help: 'Only does anything with the quotes module installed.',
          },
          requireCustomer: {
            label: 'Require a customer on every project',
            help: 'Leave it off if you open jobs for yourselves — a workshop reform, a stand for a fair.',
          },
          overrunAlertPct: {
            label: 'Warn when a job reaches this % of its budgeted cost',
            help: 'Measured against everything committed, not only what has arrived, so the warning comes while there is still something to decide. 0 turns it off.',
          },
          alertRoles: {
            label: 'Who hears that a project has gone past its budget',
            help: 'Roles, not people, so it keeps working when somebody leaves.',
          },
          retentionPct: {
            label: 'Guarantee retention held back on what you invoice',
            help: 'Usual in construction, unheard of in a workshop. 0 turns it off.',
          },
        },
      },
    },
    audit: {
      actions: {
        projects: {
          project_created: 'Project opened',
          project_updated: 'Project updated',
          project_note_added: 'Note added to the project',
          project_phase_changed: 'Project moved to another phase',
          project_active: 'Project set under way',
          project_on_hold: 'Project put on hold',
          project_draft: 'Project returned to draft',
          project_closed: 'Project closed',
          project_cancelled: 'Project cancelled',
          project_archived: 'Project archived',
          project_restored: 'Project brought back',
          project_cost_posted: 'Cost posted to the project',
          project_cost_removed: 'Cost removed from the project',
          project_document_uploaded: 'Document uploaded to the project',
          project_document_moved: 'Document moved to another folder',
          project_document_removed: 'Document removed from the project',
          project_folder_created: 'Folder created on the project',
          project_folder_removed: 'Folder removed from the project',
        },
      },
    },
  },

  fr: {
    projects: {
      title: 'Projets',
      dashboard: {
        open: 'Projets ouverts',
        openHelp: 'Combien de chantiers sont encore en cours, hors clôturés et annulés.',
        late: 'Projets en retard',
        lateHelp: 'Combien de chantiers ouverts ont dépassé la date convenue avec le client.',
        contracted: 'Contracté en cours',
        contractedHelp: 'Ce qui a été vendu sur les projets ouverts, en euros.',
        toInvoice: 'Reste à facturer',
        toInvoiceHelp: 'De ce qui a été vendu sur les projets ouverts, ce qui n\'est pas encore facturé.',
        openList: 'Chantiers en cours',
        openListHelp: 'Les projets ouverts les plus récents, avec leur client. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Les chantiers en cours, leur phase et ce qu\'ils consomment',
      detailTitle: 'Projet',
      newTitle: 'Nouveau projet',
      editTitle: 'Modifier le projet',
      actions: {
        new: 'Nouveau projet', edit: 'Modifier', postCost: 'Saisir un coût',
        close: 'Clore le projet', reopen: 'Rouvrir', cancel: 'Annuler',
        save: 'Enregistrer', remove: 'Supprimer',
      },
      status: {
        draft: 'Brouillon', active: 'En cours', on_hold: 'En pause',
        closed: 'Clos', cancelled: 'Annulé',
      },
      priority: { low: 'Basse', medium: 'Normale', high: 'Haute', critical: 'Critique' },
      nature: {
        material: 'Matériel', labour: 'Main-d\'œuvre',
        subcontract: 'Sous-traitance', other: 'Autres',
      },
      kind: { incurred: 'Déjà arrivé', committed: 'Commandé, pas encore arrivé' },
      filters: {
        oneCustomer: 'Un seul client',

        search: 'Rechercher', searchPlaceholder: 'Code, chantier, client, lieu…',
        status: 'État', allStatuses: 'Tous',
        priority: 'Priorité', allPriorities: 'Toutes',
        onlyOpen: 'Seulement ouverts', onlyLate: 'Seulement en retard',
      },
      board: { title: 'Où en est chaque chantier' },
      table: {
        code: 'Code', name: 'Projet', phase: 'Phase', due: 'Livraison',
        amount: 'Montant', margin: 'Marge', status: 'État',
      },
      kpi: {
        open: 'Projets ouverts', late: 'En retard',
        exposure: 'Coût engagé', contract: 'Contracté',
      },
      empty: {
        none: 'Pas encore de projets',
        noneHint: 'Ouvrez le premier, ou acceptez un devis et laissez-le s\'ouvrir tout seul.',
        filtered: 'Aucun résultat',
        filteredHint: 'Essayez de retirer un filtre ou de chercher autre chose.',
      },
      lateBy: '{days} jours de retard',
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      tabs: {
        overview: 'Résumé', quotes: 'Devis', documents: 'Documents',
        costs: 'Coûts', history: 'Historique',
      },
      phases: {
        title: 'Phases',
        since: 'Dans cette phase depuis le {date}',
        moved: 'Projet en {phase}',
      },
      file: { title: 'Fiche' },
      fields: {
        code: 'Code', name: 'Nom', customer: 'Client', location: 'Lieu',
        manager: 'Responsable', priority: 'Priorité', phase: 'Phase', tags: 'Étiquettes',
        description: 'Description', startDate: 'Début', dueDate: 'Livraison contractuelle',
        estimatedEndDate: 'Fin prévue', quote: 'Devis',
        contractAmount: 'Montant de vente', budgetedCost: 'Coût prévu',
      },
      money: {
        title: 'Économique',
        contract: 'Vendu (HT)', exposure: 'Engagé', margin: 'Marge',
        pendingToInvoice: 'À facturer',
        ofBudget: '{pct} du prévu',
        incurred: 'Dépensé', committed: 'Commandé, pas arrivé',
        overBudget: 'Ce projet a dépassé son coût prévu ({budget})',
      },
      ledger: {
        title: 'Coûts',
        empty: 'Pas encore de coûts',
        emptyHint: 'Saisissez le premier, ou il arrivera tout seul quand du matériel entrera pour ce projet.',
        date: 'Date', description: 'Libellé', amount: 'Montant',
        from: 'de {module}',
      },
      quotes: {
        title: 'Devis du projet',
        line: 'Poste', quantity: 'Quantité', unitPrice: 'Prix', total: 'Total',
        noLines: "Ce devis n'a pas de postes enregistrés.",
        empty: 'Aucun devis lié',
        emptyHint: 'Liez-les depuis le devis lui-même : champ « Projet » à la rédaction, ou la carte « Projet » une fois accepté.',
      },
      documents: {
        title: 'Documents',
        all: 'Tous les documents', root: 'Sans dossier',
        upload: 'Téléverser un document', uploaded: 'Document téléversé',
        newFolder: 'Nouveau dossier', folderName: 'Nom du dossier…',
        removeFolder: 'Supprimer le dossier',
        empty: 'Pas encore de documents',
        emptyFolder: 'Ce dossier est vide',
        emptyHint: 'Téléversez plans, procès-verbaux, certificats, photos… ce que le chantier doit avoir sous la main.',
        confirmRemoveTitle: 'Supprimer le document',
        confirmRemoveBody: '« {name} » sera supprimé de ce projet.',
        searchPlaceholder: 'Chercher un document…',
        name: 'Nom', size: 'Taille', uploadedAt: 'Ajouté',
        download: 'Télécharger',
        noResults: 'Rien à ce nom',
        dropHint: 'Glissez des fichiers ici pour les téléverser',
        uploadedMany: '{count} documents téléversés',
      },
      history: {
        title: 'Historique',
        notePlaceholder: 'Écrire une note sur le projet…',
        add: 'Noter',
        phase: 'Phase : {from} → {to}',
        status: 'État : {from} → {to}',
      },
      confirm: {
        closeTitle: 'Clore le projet',
        closeBody: 'La marge reste telle quelle et entre dans les rapports. Il peut être rouvert.',
        removeCostTitle: 'Supprimer l\'écriture',
        removeCostBody: 'L\'écriture de {amount} sera supprimée et le projet recalculé.',
      },
      closed: 'Projet clos',
      reopened: 'Projet rouvert',
      createdToast: 'Projet {code} créé',
      savedToast: 'Projet enregistré',
      cost: {
        title: 'Saisir un coût',
        nature: 'Nature', kind: 'Est-il arrivé ?',
        description: 'Libellé', descriptionPlaceholder: 'Grue pour le déchargement…',
        quantity: 'Quantité', unit: 'Unité', unitCost: 'Prix unitaire',
        amount: 'Montant', amountComputed: 'Quantité × prix. Calculé pour vous.',
        amountFromRate: 'Sans prix, l\'entreprise valorise les heures. Peut rester à 0.',
        hoursHint: 'p. ex. 6',
        date: 'Date', dateHelp: 'La date à laquelle le coût appartient, pas celle d\'aujourd\'hui.',
        post: 'Saisir', posted: 'Coût saisi',
      },
      form: {
        what: 'Le chantier', when: 'Les dates', money: 'L\'argent',
        namePlaceholder: 'Fermetures maison García…',
        customerPlaceholder: 'Pour qui c\'est',
        locationPlaceholder: 'Où est le chantier',
        tagsHelp: 'Séparées par des virgules.',
        tagsPlaceholder: 'chantier, urgent…',
        dueHelp: 'Celle du contrat. Celle qui a une pénalité derrière.',
        contractHelp: 'Pour combien il a été vendu, hors taxes : les coûts sont aussi hors taxes.',
        budgetHelp: 'Combien il devrait coûter. L\'alerte de dépassement se mesure contre cela.',
        nameRequired: 'Un projet a besoin d\'un nom',
      },
      search: { subtitle: 'Ouvrir le projet' },
      notifications: {
        categories: { overrun: 'Projets : dépassement de coût' },
        overrun: {
          title: 'Projet au-dessus du budget',
          body: '{code} {name} est à {ratio} % de son coût prévu',
        },
      },
    },
    modules: { projects: 'Projets' },
    assistant: {
      openers: {
        projects: {
          'whats-late': 'Quels projets sont en retard ?',
          'how-is-going': 'Comment va le projet de… ?',
          'post-hours': 'Saisis 6 heures sur le projet…',
        },
      },
    },
    permissions: {
      projects: {
        read: 'Voir les projets : phases, dates, équipe',
        write: 'Ouvrir des projets, les modifier et les faire avancer',
        costs: 'Saisir des coûts et voir budget, coût et marge',
        close: 'Clore, rouvrir, annuler et archiver des projets',
      },
    },
    settings: {
      ns: {
        projects: {
          title: 'Projets',
          subtitle: 'Comment votre entreprise mène un chantier',
          phases: {
            label: 'Les phases d\'un chantier, dans l\'ordre',
            help: 'Les vôtres : renommez, ajoutez, retirez. En renommer une laisse les projets passés par elle avec l\'ancien nom dans leur histoire.',
          },
          customFields: {
            label: 'Vos champs à vous sur la fiche',
            help: 'Par exemple : n° de commande du client, centre de coût, agence. En renommer un laisse derrière ce qui était enregistré sous l\'ancien nom.',
          },
          createFromAcceptedQuote: {
            label: 'Ouvrir un projet tout seul quand un devis est accepté',
            help: 'Ne fait quelque chose qu\'avec le module devis installé.',
          },
          requireCustomer: {
            label: 'Exiger un client sur chaque projet',
            help: 'Laissez désactivé si vous ouvrez des chantiers pour vous-mêmes : une réforme de l\'atelier, un stand.',
          },
          overrunAlertPct: {
            label: 'Avertir quand un chantier atteint ce % de son coût prévu',
            help: 'Mesuré contre l\'engagé, pas seulement contre l\'arrivé, pour avertir quand on peut encore décider. 0 l\'éteint.',
          },
          alertRoles: {
            label: 'Qui apprend qu\'un projet dépasse son budget',
            help: 'Des rôles, pas des personnes, pour que ça continue de marcher quand quelqu\'un s\'en va.',
          },
          retentionPct: {
            label: 'Retenue de garantie sur le facturé',
            help: 'Habituelle dans le bâtiment, inconnue en atelier. 0 l\'éteint.',
          },
        },
      },
    },
    audit: {
      actions: {
        projects: {
          project_created: 'Projet ouvert',
          project_updated: 'Projet modifié',
          project_note_added: 'Note ajoutée au projet',
          project_phase_changed: 'Projet passé à une autre phase',
          project_active: 'Projet mis en cours',
          project_on_hold: 'Projet mis en pause',
          project_draft: 'Projet remis en brouillon',
          project_closed: 'Projet clos',
          project_cancelled: 'Projet annulé',
          project_archived: 'Projet archivé',
          project_restored: 'Projet remis dans les listes',
          project_cost_posted: 'Coût saisi sur le projet',
          project_cost_removed: 'Coût supprimé du projet',
          project_document_uploaded: 'Document téléversé sur le projet',
          project_document_moved: 'Document déplacé de dossier',
          project_document_removed: 'Document supprimé du projet',
          project_folder_created: 'Dossier créé sur le projet',
          project_folder_removed: 'Dossier supprimé du projet',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

export const messages = withToolLabels(withAnalytics(baseMessages, 'projects'))
